import React, { useLayoutEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { Cube, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { SEO } from '../components/SEO';
const ACCENT = '#0F172A';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
function LoomEmbed({
  loomId,
  caption,
  aspect = 64.55089820359281
}: {
  loomId: string;
  caption?: string;
  aspect?: number;
}) {
  return <div className="bg-white rounded-2xl overflow-hidden border border-slate-200" data-id="element-621" data-mp="1e0k46jkmupov">
      <div style={{
      padding: `${aspect}% 0 0 0`,
      position: 'relative'
    }} data-id="element-622" data-mp="2bn9f5ixbccmv">
        <iframe src={`https://www.loom.com/embed/${loomId}`} title={caption || 'Video'} frameBorder="0" allowFullScreen style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} data-id="element-623" data-mp="26p7uitk8o59a" />
      </div>
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-624" data-mp="la25duqxzah9">
          {caption}
        </p>}
    </div>;
}
const processSteps = [{
  number: '01',
  title: 'Origin: From Context to Amira',
  desc: 'Amira began as Context, an early 2025 concept built around something I do naturally: watch how people work, learn their rhythm quickly, and see where a workflow is getting in its own way. I pay attention to how people build, think, react to a new idea, and hand work to the next person. The product needed to do the same. It should know what to automate, what to coach, and what must stay human. Human-in-the-loop was never a limitation. It was the feedback mechanism that makes the system smarter without taking judgment away from the team.'
}, {
  number: '02',
  title: 'Problem: Good work lost its context between tools',
  desc: 'Teams were not short on tools. They were losing the thread between them. Ideas began in Miro, early prototypes moved through Magic Patterns, feedback lived in Dovetail, polish happened in Figma, handoff details drifted into Jira and Slack, and engineering had to reconstruct the story. Watching that thread break over and over shaped the core requirement: whatever the product became, it had to follow work across tools, keep the context intact, and put a human review step at every point where quality is decided.'
}, {
  number: '03',
  title: 'Research: Mirror first, coach second',
  desc: 'The first version did not jump straight to automation. Context started in Mirror mode, observing gaps, repetitions, and small workarounds across the tools a team already used. It could surface a pattern without judging it: designers might mark work ready in Figma, then spend hours rebuilding the missing context in Slack. Coach mode came next. Instead of prescribing a new process, it asked the team whether they wanted to test a better one. The product was designed to make people better at the tools they already use, then earn the right to automate predictable work later.'
}, {
  number: '04',
  title: 'Design: A human-guided iteration loop',
  desc: 'I designed the working surfaces around the moments where quality is usually lost. A researcher reviews and corrects AI-summarized Dovetail feedback before it becomes an insight. A designer uses human-refined visual cues as a starting point, not a finished answer. In the Magic Patterns to Figma bridge, the system can suggest component mappings and cleaner layer structure, while the designer confirms what belongs in the design system and explains the correction. Those corrections become the learning loop. The goal was not perfect automation on day one. It was a better starting point that learns from expert decisions.'
}, {
  number: '05',
  title: 'Build: From workflow intelligence to agent harness',
  desc: 'That product thinking evolved into Amira, a proprietary, portable harness for building and orchestrating agents. I carried the same principles into a custom Design Studio, model-agnostic routing, browser automation, persistent project memory, and the Dream Engine, a human-gated self-improvement loop. Amira is built with Next.js and Python, uses WebAssembly components for portable agents, and is orchestrated through Eko. It stands on its own as a harness, and it connects into the Silo backend when work needs stronger controls. Capability and permission are never treated as the same thing.'
}];
export function CaseStudyAmira({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <SEO title="Amira - AI Agent Harness Case Study" description="Designing Amira, a proprietary, portable AI agent harness that stands alone and connects into the Silo backend. A case study in human-guided agent design by Mateo Cohen." keywords={['AI Agent Design', 'Agent Harness', 'AI Product Design', 'UX Case Study', 'Human-in-the-loop AI']} url="https://mateocohen.com/case-study/amira" type="article" image="https://cdn.magicpatterns.com/uploads/x4S7EPV4CN7QuEvhUbsnuF/mockuuups-macbook-pro-16-inch-mockup-on-a-textured-gray-surface-with-dramatic-lighting.jpg" data-id="element-625" data-mp="fpkhwaha6zst" />
      <ReadingProgress color={ACCENT} data-id="element-626" data-mp="2ixuji74436oy" />
      {!isOverlay && <CustomCursor data-id="element-627" data-mp="2iz5q0eoxysjr" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-628" data-mp="2nvw2yt5g980y">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-629" data-mp="2ny8tvvt6qsq5">
          {!isOverlay && <CaseStudyHeader data-id="element-630" data-mp="17gl17nwpykp8" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-631" data-mp="1chihtw22z6om" />}

          <main className="flex-1 w-full pb-32" data-id="element-632" data-mp="1cik5d00a9ln8">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-633" data-mp="3qnp4luh9msx0">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-634" data-mp="37y0nb46c7b04">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-635" data-mp="2k8fsno0tmpb0" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-636" data-mp="3e1s1h67mbdub">
                <Cube className="w-8 h-8" weight="light" style={{
                color: ACCENT
              }} data-id="element-637" data-mp="3e2tpeb9qfutg" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif",
              color: ACCENT
            }} data-id="element-638" data-mp="3n15do6n4mgiq">
                Amira
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-639" data-mp="5p4csedavico">
                An adaptive, human-guided copilot for the way teams already
                build.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-640" data-mp="tqzkej4i9jbm">
                Amira grew out of Context, my early 2025 concept for an AI that
                learns how teams really work across design, research, and
                delivery. As Head of AI Design Strategy and Research at Silo AI,
                I turned that thinking into a proprietary, portable agent
                harness. On its own, Amira can do most of what any large harness
                can do today: design, learn how to work with AI, build agents,
                and create sites or even whole companies. Its key difference is
                what it connects into, Silo, the backend we have been building
                for nine months for where digital building is going. Amira
                launches at the end of July 2026, with Silo announced soon
                after.
              </p>
            </motion.section>

            {/* HERO MEDIA CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-641" data-mp="mp9q0g5r9zjj">
              <div className="bg-slate-100 rounded-2xl p-4 sm:p-8 md:p-10" data-id="element-642" data-mp="15n3y8rz2870v">
                <LoomEmbed loomId="13c50013c4dc4b41880bd2f09c9bb0c2" caption="Recorded demo of the Amira fork" aspect={64.6412037037037} data-id="element-643" data-mp="15mu916lua3w8" />
              </div>

              {/* Note on the demo fork */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 sm:p-8 mt-6" data-id="element-644" data-mp="1tdgecq4gla2j">
                <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{
                fontFamily: "'Ronzino', sans-serif",
                color: ACCENT
              }} data-id="element-645" data-mp="1oewqula7vq82">
                  A note on this demo
                </div>
                <p className="text-base text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-646" data-mp="1y0o8kbndfbdh">
                  What you are watching is the demo fork of Amira, the free
                  version we are giving out so anyone can design, learn how to
                  work with AI, and build agents. A second, more robust and
                  polished version attaches as the front end to Silo. We built
                  two on purpose: we want people to get familiar with Amira
                  first, then be surprised by what Amira and Silo can do
                  together. The demo fork is being cleaned up to production
                  level with the dev team over the next two weeks, so every
                  feature is ready for users to play with along clear UX routes.
                  It stays free, while the Silo and Amira setup follows as a
                  very fast follow.
                </p>
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-647" data-mp="2y7jlxe90ogpx">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-648" data-mp="3hwbwt781bf4i">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-12" data-id="element-649" data-mp="2akbf2rvxwzls">
                  <div data-id="element-650" data-mp="26igfbrtnqua4">
                    <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-651" data-mp="26hyk8yiqem3s">
                      Product
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-652" data-mp="uzj8czno86wu">
                      Amira (Silo AI)
                    </div>
                  </div>
                  <div data-id="element-653" data-mp="1drbzeoj6ji1x">
                    <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-654" data-mp="1dr3pn5icssuy">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-655" data-mp="3kibf7gdi6u3m">
                      Head of AI Design Strategy and Research
                    </div>
                  </div>
                  <div data-id="element-656" data-mp="2u6g7bn5zrajy">
                    <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-657" data-mp="2u811hboe8rha">
                      Timeline
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-658" data-mp="2g8rn8hjr07fy">
                      2025 to 2026
                    </div>
                  </div>
                  <div data-id="element-659" data-mp="3uee98r1hm695">
                    <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-660" data-mp="3ue4ih18mbv2e">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-661" data-mp="3pdqmwio7edl9">
                      AI design strategy, Workflow research, Agent UX,
                      Orchestration, Design systems
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-662" data-mp="2spau8dpsro3x">
                  <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: ACCENT
                }} data-id="element-663" data-mp="2srwxw29khjgv">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-664" data-mp="tic83ts4mm29">
                    Mateo Cohen (AI Design Strategy and Research)
                  </div>
                </div>
              </div>
            </motion.section>

            {/* PRINCIPLE-OVERLAY CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-665" data-mp="akj38lxy0228">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-666" data-mp="28foim3jdjv5s">
                <div className="absolute inset-0 bg-slate-900" aria-hidden="true" data-id="element-667" data-mp="2cfqn0kghv2or" />

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-668" data-mp="2m73g1fe13ywd">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-669" data-mp="2m97hlqogglth">
                    "The application does not make itself better. It makes you
                    better at the tools you already use."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-670" data-mp="23e912lgeo6s5">
                    The original Context principle
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS (STAR) */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-671" data-mp="dkruwtqwbw3x">
              {processSteps.map(step => <motion.div key={step.number} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-672" data-mp="8rowa779hwgv">
                  <div className="flex items-center gap-3 mb-4" data-id="element-673" data-mp="rk9aqjm7rz15">
                    <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: ACCENT
                }} data-id="element-674" data-mp="rksvs975v3je">
                      {step.number}
                    </span>
                    <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: ACCENT
                }} aria-hidden="true" data-id="element-675" data-mp="1dsyb9ty5y56f" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-medium mb-6" style={{
                fontFamily: "'Gefika', sans-serif",
                color: ACCENT
              }} data-id="element-676" data-mp="q2u4jdpohpb7">
                    {step.title}
                  </h3>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-677" data-mp="l3hmr7dnz8fh">
                    {step.desc}
                  </p>
                </motion.div>)}

              {/* Tech stack callout */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-678" data-mp="14fwb454ksd54">
                <div className="bg-slate-100 rounded-lg p-6 sm:p-8" data-id="element-679" data-mp="ungq0yxzf8g0">
                  <div className="text-xs uppercase tracking-[0.2em] mb-4" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: ACCENT
                }} data-id="element-680" data-mp="1ifoybqgw8b8c">
                    Under the hood
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-681" data-mp="1di5lmuzug6ii">
                    {['Product surfaces', 'Brain and orchestration', 'Tools', 'Agents', 'Persistent memory', 'Learning loop', 'Runtime', 'Evidence and approval', 'Next.js + Python', 'WebAssembly portable agents', 'Eko orchestration', 'Silo backend'].map(tag => <span key={tag} className="text-[11px] tracking-[0.12em] uppercase text-slate-700 border border-slate-300 rounded-full px-3 py-1.5" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-682" data-mp="2l5x7r8tvap9p">
                        {tag}
                      </span>)}
                  </div>
                </div>
              </motion.div>

              {/* Outcome (Result) */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-683" data-mp="19te6rnty4c0e">
                <div className="flex items-center gap-3 mb-4" data-id="element-684" data-mp="3in5w5k606qbd">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: ACCENT
                }} data-id="element-685" data-mp="3inxu31n2p04t">
                    06
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: ACCENT
                }} aria-hidden="true" data-id="element-686" data-mp="2uzelsm40wncq" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium mb-6" style={{
                fontFamily: "'Gefika', sans-serif",
                color: ACCENT
              }} data-id="element-687" data-mp="f5e2808b3waj">
                  Result: a harness that stands alone, built for Silo
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-688" data-mp="1zqkpy005n99n">
                  <div data-id="element-689" data-mp="1uvewmg4fasbj">
                    <div className="text-2xl sm:text-3xl font-light mb-2" style={{
                    fontFamily: "'Gefika', sans-serif",
                    color: ACCENT
                  }} data-id="element-690" data-mp="1pz6lddtcjj9n">
                      Portable harness
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em]" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-691" data-mp="1bzfc1qdryr1z">
                      Launching end of July 2026
                    </div>
                  </div>
                  <div data-id="element-692" data-mp="1z05rxi57f46w">
                    <div className="text-2xl sm:text-3xl font-light mb-2" style={{
                    fontFamily: "'Gefika', sans-serif",
                    color: ACCENT
                  }} data-id="element-693" data-mp="1z1qkj27ykd24">
                      Model-agnostic
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em]" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-694" data-mp="1u35nfbo069hw">
                      Bring your own key
                    </div>
                  </div>
                  <div data-id="element-695" data-mp="1bbmi2y6iajcp">
                    <div className="text-2xl sm:text-3xl font-light mb-2" style={{
                    fontFamily: "'Gefika', sans-serif",
                    color: ACCENT
                  }} data-id="element-696" data-mp="1b8qnnjtvact0">
                      Human-gated
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em]" style={{
                    fontFamily: "'Ronzino', sans-serif",
                    color: ACCENT
                  }} data-id="element-697" data-mp="dpjrcm5wt9ji">
                      Secure by default
                    </div>
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-698" data-mp="wo4ds0wuvqqt">
                  Amira launches at the end of July 2026 as a proprietary,
                  portable harness you can use on its own to design, learn how
                  to work with AI, and build agents, sites, or even companies.
                  When you want more, it connects into Silo, the backend we have
                  spent nine months building for the future of AI and where we
                  see digital building going. The original Context loop remains
                  the point: observe how work really moves, reflect it back
                  clearly, learn from expert corrections, and only automate when
                  the team is ready to trust the result.
                </p>
              </motion.div>
            </section>

            <CaseStudyNavigation accent={ACCENT} previousCase={{
            category: 'AI Comparison',
            title: 'Conversational AI',
            slug: 'ai-comparison'
          }} data-id="element-699" data-mp="2a7emjd9kn9qf" />
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-700" data-mp="ksgfvc8wkygw" />
      <ChatDrawer data-id="element-701" data-mp="kszwmpmthgtc" />
      <MobileBottomCTA data-id="element-702" data-mp="kti3shb0pfg1" />
      {!isOverlay && <Footer data-id="element-703" data-mp="kukqse57yxqo" />}
    </>;
}